import { DECOYS } from '../data';
import { Backlog } from './Posters';
import Quiz, { type Round } from './Quiz';
import { entriesOf, ROUNDS, sample, shuffle, type Entry } from './game';

// The Backlog takes HITS right answers to defeat; HEARTS wrong answers end the run.
// With ROUNDS questions you can afford HEARTS - 1 mistakes and still win.
const HITS = 6;
const HEARTS = 3;
// Points per hit: 10, plus up to 5 for answering fast. The health bar only counts hits.
const ANSWER_MS = 12000;
const MAX_POINTS = 15;
const MISS_PENALTY = 5;
const pool = entriesOf('Games').filter(e => e.trivia);

type BossRound = Round & { question: string; fact?: string };

// Games that are easy to mix up (same series or same studio) always show up as each other's wrong answers.
const RIVALS = [
  ["Marvel's Spider-Man", "Marvel's Spider-Man 2", 'Spider-Man: Miles Morales'],
  ['God of War (2018)', 'God of War Ragnarök'],
  ['Super Mario Sunshine', 'Super Mario Odyssey'],
  ['inFamous Second Son', 'Ghost of Tsushima'], // both Sucker Punch
  ['World of Warcraft', 'Overwatch'], // both Blizzard
];

// Harder than the other games: two other favorites (rivals first) and one non-favorite.
function hardOptions(e: Entry): string[] {
  const rivals = RIVALS.find(g => g.includes(e.name))?.filter(n => n !== e.name) ?? [];
  const rest = pool.map(x => x.name).filter(n => n !== e.name && !rivals.includes(n));
  const favorites = [...shuffle(rivals), ...shuffle(rest)].slice(0, 2);
  return shuffle([e.name, ...favorites, ...sample(DECOYS.Games, 1)]);
}

const points = (ms: number) => 10 + Math.max(0, Math.ceil(((ANSWER_MS - ms) / ANSWER_MS) * (MAX_POINTS - 10)));

const makeRounds = (): BossRound[] => sample(pool, ROUNDS).map(e => ({
  key: e.name, answer: e.name, options: hardOptions(e), question: 'Which game is this?', fact: e.trivia,
}));

export default function BossFight({ onExit }: { onExit: () => void }) {
  return (
    <Quiz<BossRound>
      game="boss"
      makeRounds={makeRounds}
      maxScore={HITS * MAX_POINTS}
      scoreFor={points}
      missPenalty={MISS_PENALTY}
      over={({ hits, misses }) => hits >= HITS || misses >= HEARTS}
      hud={({ hits, misses, index, total }) => {
        const hp = Math.max(0, HITS - hits);
        return (
          <div className="boss-hud">
            <div className="boss-name">
              <span className="boss-title"><Backlog size={4} color="#FF3DF2" /> The Backlog</span>
              <span className="boss-hearts" aria-label={`${HEARTS - misses} of ${HEARTS} hearts left`}>
                {Array.from({ length: HEARTS }, (_, i) => <span key={i} className={i < HEARTS - misses ? 'is-full' : ''}>♥</span>)}
              </span>
            </div>
            <div className="boss-hp" role="meter" aria-label="Boss health" aria-valuemin={0} aria-valuemax={HITS} aria-valuenow={hp}>
              <div style={{ width: `${(hp / HITS) * 100}%` }} />
            </div>
            <span className="boss-round">Question {Math.min(index + 1, total)}/{total}</span>
          </div>
        );
      }}
      renderStage={round => (
        <div className="boss-stage">
          <Backlog size={9} color="#B14CFF" />
          <div className="boss-speech">
            <span className="game-label">{round.question}</span>
            {round.fact && <p className="boss-fact">{round.fact}</p>}
          </div>
        </div>
      )}
      verdict={({ hits, misses }) =>
        hits >= HITS
          ? misses === 0 ? 'Flawless victory! The Backlog is cleared. (Mine never is.)' : 'Victory! The Backlog is defeated, for now.'
          : 'Game over. The Backlog grows stronger. Insert coin to try again.'}
      onExit={onExit}
    />
  );
}
